import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cx } from '@/lib/cx';
import { t, tp } from '@/lib/i18n';
import s from './DataTree.module.css';

const isBranch = (v: unknown): v is Record<string, unknown> | unknown[] => typeof v === 'object' && v !== null;

/** snake_case / camelCase keys read as words: "median_rpm" → "median rpm". */
function humanKey(k: string): string {
  return k
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
}

function Leaf({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className={s.null}>—</span>;
  if (typeof value === 'boolean') return <span className={s.bool}>{value ? t('yes') : t('no')}</span>;
  if (typeof value === 'number') return <span className={s.num}>{Number.isInteger(value) ? value : Math.round(value * 1000) / 1000}</span>;
  return <span className={s.str}>{String(value)}</span>;
}

function Node({ name, value, depth, openDepth }: { name: string; value: unknown; depth: number; openDepth: number }) {
  const [open, setOpen] = useState(depth < openDepth);
  if (!isBranch(value)) {
    return (
      <li className={s.row}>
        <span className={s.key}>{name}</span>
        <Leaf value={value} />
      </li>
    );
  }
  const entries = Array.isArray(value) ? value.map((v, i) => [String(i + 1), v] as const) : Object.entries(value);
  const count = Array.isArray(value) ? tp(entries.length, '{count} item', '{count} items') : tp(entries.length, '{count} field', '{count} fields');
  return (
    <li className={s.branch}>
      <button type="button" className={s.toggle} aria-expanded={open} onClick={() => setOpen(!open)}>
        <ChevronRight className={cx(s.chevron, open && s.open)} aria-hidden />
        <span className={s.key}>{name}</span>
        <span className={s.count}>{count}</span>
      </button>
      {open && entries.length > 0 && (
        <ul className={s.list}>
          {entries.slice(0, 500).map(([k, v]) => (
            <Node key={k} name={Array.isArray(value) ? `#${k}` : humanKey(k)} value={v} depth={depth + 1} openDepth={openDepth} />
          ))}
          {entries.length > 500 && <li className={s.more}>{tp(entries.length - 500, '{count} more not shown', '{count} more not shown')}</li>}
        </ul>
      )}
    </li>
  );
}

/**
 * Structured data (evidence, parsed documents, audit results) as a collapsible tree of
 * human-readable keys and values. Text only — nothing is rendered as HTML (RULES §SEC-4).
 * Import it from '@/ui/DataTree'.
 */
export function DataTree({ data, openDepth = 1, label, className }: { data: unknown; openDepth?: number; label: string; className?: string }) {
  const entries = isBranch(data) ? (Array.isArray(data) ? data.map((v, i) => [`#${i + 1}`, v] as const) : Object.entries(data)) : [];
  if (!isBranch(data)) return <Leaf value={data} />;
  if (!entries.length) return <p className={s.empty}>{t('No data')}</p>;
  return (
    <ul className={cx(s.tree, className)} aria-label={label}>
      {entries.map(([k, v]) => (
        <Node key={k} name={Array.isArray(data) ? k : humanKey(k)} value={v} depth={0} openDepth={openDepth} />
      ))}
    </ul>
  );
}
